import React from 'react';
import { Card, Statistic, Row, Col, Skeleton, Progress, Tooltip, Badge, Tag, Alert } from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  UnlockOutlined,
  BuildOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

interface StatsData {
  total_questionarios: number;
  questionarios_concluidos: number;
  total_titulares: number;
  categorias_titulares?: number;
  dados_sensiveis: number;
  dados_comuns: number;
  areas_mapeadas: number;
  total_processos?: number;
  total_perguntas?: number;
  taxa_conclusao?: number;
}

interface MapeamentosStatsProps {
  stats: StatsData;
  loading?: boolean;
  showAdvanced?: boolean;
  clienteId?: string;
  clienteNome?: string;
}

const MapeamentosStats: React.FC<MapeamentosStatsProps> = ({ 
  stats, 
  loading = false, 
  showAdvanced = false,
  clienteId,
  clienteNome
}) => {

  if (loading) {
    return (
      <div>
        {/* Skeleton para banner de contexto */}
        {clienteId && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Skeleton active paragraph={{ rows: 1 }} />
          </Card>
        )}
        
        {/* Skeleton para cards de estatísticas */}
        <Row gutter={[16, 16]}>
          {[...Array(6)].map((_, i) => (
            <Col xs={24} sm={12} lg={8} key={i}>
              <Card style={{ height: 180 }}>
                <Skeleton active />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  const taxaConclusao = stats.taxa_conclusao || 
    (stats.total_questionarios > 0 
      ? Math.round((stats.questionarios_concluidos / stats.total_questionarios) * 100)
      : 0);
  
  const totalDados = stats.dados_sensiveis + stats.dados_comuns;
  const percentualDadosSensiveis = totalDados > 0 
    ? Math.round((stats.dados_sensiveis / totalDados) * 100) 
    : 0;

  // Funções auxiliares
  const getCorTaxaConclusao = (taxa: number): string => {
    if (taxa >= 90) return '#52c41a';
    if (taxa >= 70) return '#faad14';
    if (taxa >= 50) return '#fa8c16';
    return '#f5222d';
  };

  const getStatusConformidade = (taxa: number) => {
    if (taxa >= 90) return { text: 'Excelente', color: 'success' as const };
    if (taxa >= 70) return { text: 'Bom', color: 'processing' as const };
    if (taxa >= 50) return { text: 'Regular', color: 'warning' as const };
    return { text: 'Atenção', color: 'error' as const };
  };

  const conformidade = getStatusConformidade(taxaConclusao);

  // Recomendações contextuais
  const getContextualRecommendations = (): React.ReactNode[] => {
    const recommendations: React.ReactNode[] = [];

    if (stats.total_questionarios === 0) {
      recommendations.push(
        <Tag key="no-questionarios" color="red">
          🚨 {clienteId ? `Nenhum questionário para ${clienteNome || 'este cliente'}` : 'Crie seu primeiro questionário de mapeamento'}
        </Tag>
      );
    }

    if (stats.total_titulares === 0 && stats.total_questionarios > 0) {
      recommendations.push(
        <Tag key="no-titulares" color="red">
          ⚠️ {clienteId ? `Cadastre titulares para ${clienteNome || 'este cliente'}` : 'Cadastre titulares de dados'}
        </Tag>
      );
    }

    if (stats.dados_sensiveis === 0 && stats.total_questionarios > 0) {
      recommendations.push(
        <Tag key="no-dados-sensiveis" color="orange">
          💡 {clienteId ? `Mapear dados sensíveis para ${clienteNome || 'este cliente'}` : 'Considere mapear dados sensíveis'}
        </Tag>
      );
    }

    if (taxaConclusao < 50 && stats.total_questionarios > 0) {
      recommendations.push(
        <Tag key="baixa-conclusao" color="volcano">
          📋 {clienteId ? `Finalize questionários de ${clienteNome || 'este cliente'}` : 'Finalize mais questionários'}
        </Tag>
      );
    }

    return recommendations;
  };

  // Estilo comum para todos os cards de estatísticas
  const cardStyle = {
    borderRadius: 8,
    height: 180, // Altura fixa para todos os cards
    display: 'flex',
    flexDirection: 'column' as const
  };

  const cardBodyStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'space-between'
  };

  return (
    <div>
      {/* Status de conformidade em destaque */}
      {stats.total_questionarios > 0 && (
        <Card 
          size="small" 
          style={{ 
            marginBottom: 16, 
            borderLeft: `4px solid ${getCorTaxaConclusao(taxaConclusao)}` 
          }}
        >
          <Row align="middle">
            <Col flex="auto">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SafetyCertificateOutlined 
                  style={{ 
                    color: getCorTaxaConclusao(taxaConclusao),
                    fontSize: 16 
                  }} 
                />
                <span>
                  <strong>Status de Conformidade{clienteId ? ` - ${clienteNome || 'Cliente'}` : ''}:</strong> {conformidade.text}
                  {taxaConclusao < 100 && (
                    <span style={{ color: '#8c8c8c' }}>
                      {` - Faltam ${100 - taxaConclusao}% para conclusão`}
                    </span>
                  )}
                </span>
              </div>
            </Col>
            <Col>
              <Progress 
                type="circle" 
                percent={taxaConclusao} 
                width={40}
                strokeColor={getCorTaxaConclusao(taxaConclusao)}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Alerta para dados vazios */}
      {stats.total_questionarios === 0 && (
        <Alert
          message={clienteId ? `Nenhum Dado para ${clienteNome || 'Cliente'}` : "Nenhum Dado Encontrado"}
          description={
            clienteId 
              ? `Não foram encontrados dados para ${clienteNome || 'este cliente'}. Verifique se há dados cadastrados.`
              : "Nenhum questionário foi encontrado. Comece criando seu primeiro questionário de mapeamento LGPD."
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Cards de estatísticas principais */}
      <Row gutter={[16, 16]}>
        {/* Questionários */}
        <Col xs={24} sm={12} lg={8}>
          <Tooltip title={`${stats.questionarios_concluidos} de ${stats.total_questionarios} questionários concluídos`}>
            <Card hoverable style={cardStyle} bodyStyle={cardBodyStyle}>
              <div>
                <Statistic
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Questionários</span>
                      <FileTextOutlined style={{ color: '#1890ff' }} />
                    </div>
                  }
                  value={stats.total_questionarios}
                  valueStyle={{ color: '#1890ff' }}
                />
                <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
                  {stats.questionarios_concluidos} concluídos
                </div>
              </div>
              <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                <Progress 
                  percent={taxaConclusao} 
                  size="small" 
                  strokeColor={getCorTaxaConclusao(taxaConclusao)}
                  showInfo={false}
                />
              </div>
            </Card>
          </Tooltip>
        </Col>

        {/* Titulares */}
        <Col xs={24} sm={12} lg={8}>
          <Tooltip title={`${stats.total_titulares} titulares cadastrados${stats.categorias_titulares ? ` em ${stats.categorias_titulares} categorias` : ''}`}>
            <Card hoverable style={cardStyle} bodyStyle={cardBodyStyle}>
              <div>
                <Statistic
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Titulares</span>
                      <UserOutlined style={{ color: '#52c41a' }} />
                    </div>
                  }
                  value={stats.total_titulares}
                  valueStyle={{ color: '#52c41a' }}
                />
                <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
                  {stats.categorias_titulares ? `${stats.categorias_titulares} categorias` : 'cadastrados'}
                </div>
              </div>
              {/* Div vazia para manter o espaçamento */}
              <div style={{ marginTop: 'auto', paddingTop: '12px', height: '22px' }}>
                {/* Espaço reservado para manter altura consistente */}
              </div>
            </Card>
          </Tooltip>
        </Col>

        {/* Dados Sensíveis */}
        <Col xs={24} sm={12} lg={8}>
          <Tooltip title={`${stats.dados_sensiveis} tipos de dados sensíveis (${percentualDadosSensiveis}% do total)`}>
            <Card 
              hoverable 
              style={{ 
                ...cardStyle,
                border: stats.dados_sensiveis > 0 ? '1px solid #ff7a45' : undefined
              }}
              bodyStyle={cardBodyStyle}
            >
              <div>
                <Statistic
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Dados Sensíveis</span>
                      <LockOutlined style={{ color: '#f5222d' }} />
                    </div>
                  }
                  value={stats.dados_sensiveis}
                  valueStyle={{ color: '#f5222d' }}
                />
                <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
                  {percentualDadosSensiveis}% do total
                </div>
              </div>
              <div style={{ marginTop: 'auto', paddingTop: '12px', minHeight: '22px' }}>
                {stats.dados_sensiveis > 0 && (
                  <Tag color="red" style={{ fontSize: '11px' }}>
                    <ExclamationCircleOutlined /> Requer atenção
                  </Tag>
                )}
              </div>
            </Card>
          </Tooltip>
        </Col>

        {/* Dados Comuns */}
        <Col xs={24} sm={12} lg={8}>
          <Tooltip title={`${stats.dados_comuns} tipos de dados comuns`}>
            <Card hoverable style={cardStyle} bodyStyle={cardBodyStyle}>
              <div>
                <Statistic
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Dados Comuns</span>
                      <UnlockOutlined style={{ color: '#52c41a' }} />
                    </div>
                  }
                  value={stats.dados_comuns}
                  valueStyle={{ color: '#52c41a' }}
                />
                <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
                  {100 - percentualDadosSensiveis}% do total
                </div>
              </div>
              {/* Div vazia para manter o espaçamento */}
              <div style={{ marginTop: 'auto', paddingTop: '12px', height: '22px' }}>
                {/* Espaço reservado para manter altura consistente */}
              </div>
            </Card>
          </Tooltip>
        </Col>

        {/* Áreas Mapeadas */}
        <Col xs={24} sm={12} lg={8}>
          <Tooltip title={`${stats.areas_mapeadas} áreas/departamentos mapeados`}>
            <Card hoverable style={cardStyle} bodyStyle={cardBodyStyle}>
              <div>
                <Statistic
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Áreas Mapeadas</span>
                      <BuildOutlined style={{ color: '#722ed1' }} />
                    </div>
                  }
                  value={stats.areas_mapeadas}
                  valueStyle={{ color: '#722ed1' }}
                />
                <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
                  departamentos
                </div>
              </div>
              {/* Div vazia para manter o espaçamento */}
              <div style={{ marginTop: 'auto', paddingTop: '12px', height: '22px' }}>
                {/* Espaço reservado para manter altura consistente */}
              </div>
            </Card>
          </Tooltip>
        </Col>

        {/* Conformidade LGPD */}
        <Col xs={24} sm={12} lg={8}>
          <Tooltip title={`Nível de conformidade: ${conformidade.text} (${taxaConclusao}%)`}>
            <Card hoverable style={cardStyle} bodyStyle={cardBodyStyle}>
              <div>
                <Statistic
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Conformidade LGPD</span>
                      <SafetyCertificateOutlined style={{ color: getCorTaxaConclusao(taxaConclusao) }} />
                    </div>
                  }
                  value={taxaConclusao}
                  suffix="%"
                  valueStyle={{ color: getCorTaxaConclusao(taxaConclusao) }}
                />
                <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
                  Status: {conformidade.text}
                </div>
              </div>
              <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                <Badge status={conformidade.color} text={conformidade.text} />
              </div>
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Recomendações contextuais */}
      {(stats.total_questionarios === 0 || stats.total_titulares === 0 || taxaConclusao < 50 || stats.dados_sensiveis === 0) && (
        <Card 
          size="small" 
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
              <span>Recomendações{clienteId ? ` para ${clienteNome || 'Cliente'}` : ''}</span>
            </div>
          }
          style={{ marginTop: 16 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {getContextualRecommendations()}
          </div>
        </Card>
      )}

      {/* Análise detalhada (modo avançado) */}
      {showAdvanced && stats.total_questionarios > 0 && (
        <Card 
          size="small" 
          title="Análise Detalhada"
          style={{ marginTop: 16 }}
        >
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <strong>Cobertura de Dados:</strong> {totalDados > 0 ? 
                  `${percentualDadosSensiveis}% sensíveis, ${100 - percentualDadosSensiveis}% comuns` : 
                  'Nenhum dado mapeado'
                }
              </div>
            </Col>
            <Col span={12}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <strong>Eficiência:</strong> {stats.total_questionarios > 0 ? 
                  `${(stats.total_titulares / stats.total_questionarios).toFixed(1)} titulares/questionário` : 
                  'N/A'
                }
              </div>
            </Col>
            <Col span={12}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <strong>Diversidade:</strong> {stats.categorias_titulares || 0} categoria(s) de titulares
              </div>
            </Col>
            <Col span={12}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <strong>Cobertura de Áreas:</strong> {stats.areas_mapeadas} área(s) mapeada(s)
              </div>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default MapeamentosStats;